<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useSearch } from '@/composables/useSearch';
import SearchSuggestionsDropdown from '@/sites/tfs/components/public/SearchSuggestionsDropdown.vue';

type Props = {
  autofocus?: boolean;
  size?: 'default' | 'large';
};

const props = withDefaults(defineProps<Props>(), {
  autofocus: false,
  size: 'default',
});

const emit = defineEmits<{
  (e: 'navigated'): void;
}>();

const router = useRouter();
const { t } = useI18n();
const containerRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const inputValue = ref('');
const isDropdownVisible = ref(false);
const selectedIndex = ref(0);
const suggestionsListboxId = 'search-suggestions-listbox';

const { suggestions, loading, debouncedAutocomplete } = useSearch();

const hasSuggestions = computed(() => suggestions.value.length > 0);
const activeDescendantId = computed(() => {
  if (!isDropdownVisible.value || !hasSuggestions.value) {
    return undefined;
  }

  return `${suggestionsListboxId}-option-${selectedIndex.value}`;
});

watch(inputValue, (newValue) => {
  if (newValue.trim().length < 3) {
    isDropdownVisible.value = false;
    return;
  }

  isDropdownVisible.value = true;
  selectedIndex.value = 0;
  debouncedAutocomplete(newValue);
});

const navigateToSearch = (query: string): void => {
  const normalizedQuery = query.trim();
  if (normalizedQuery.length < 3) {
    return;
  }

  isDropdownVisible.value = false;
  emit('navigated');
  void router.push({
    path: '/search',
    query: { q: normalizedQuery },
  });
};

const handleSuggestionSelect = (title: string, url?: string): void => {
  isDropdownVisible.value = false;
  inputValue.value = title;

  if (url) {
    emit('navigated');
    void router.push(url);
    return;
  }

  navigateToSearch(title);
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (!isDropdownVisible.value || !hasSuggestions.value) {
    if (event.key === 'Enter') {
      navigateToSearch(inputValue.value);
    }
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    selectedIndex.value = Math.min(selectedIndex.value + 1, suggestions.value.length - 1);
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0);
    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    const suggestion = suggestions.value[selectedIndex.value];
    if (suggestion) {
      handleSuggestionSelect(suggestion.title, suggestion.url);
      return;
    }
    navigateToSearch(inputValue.value);
    return;
  }

  if (event.key === 'Escape') {
    isDropdownVisible.value = false;
  }
};

const handleDocumentClick = (event: MouseEvent): void => {
  if (!containerRef.value) {
    return;
  }

  const target = event.target;
  if (target instanceof Node && !containerRef.value.contains(target)) {
    isDropdownVisible.value = false;
  }
};

document.addEventListener('click', handleDocumentClick);

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
});

const focusInput = (): void => {
  inputRef.value?.focus();
};

onMounted(() => {
  if (props.autofocus) {
    void nextTick(() => {
      focusInput();
    });
  }
});

watch(
  () => props.autofocus,
  (shouldFocus) => {
    if (!shouldFocus) {
      return;
    }

    void nextTick(() => {
      focusInput();
    });
  }
);

defineExpose({
  focusInput,
});
</script>

<template>
  <div ref="containerRef" class="relative w-full">
    <div class="relative">
      <input
        ref="inputRef"
        v-model="inputValue"
        type="text"
        :placeholder="t('search.placeholder')"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isDropdownVisible && (hasSuggestions || loading)"
        :aria-controls="suggestionsListboxId"
        :aria-activedescendant="activeDescendantId"
        :class="[
          'w-full rounded-lg border border-noir-700 bg-noir-900 px-4 text-cream-100 placeholder-noir-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-noir-900',
          props.size === 'large' ? 'min-h-[52px] py-3 pr-14 text-base' : 'min-h-[44px] py-2.5 pr-12',
        ]"
        @keydown="handleKeydown"
      />

      <button
        type="button"
        :aria-label="t('search.go')"
        class="absolute inset-y-0 right-2 my-auto min-h-[44px] min-w-[44px] rounded-md px-2 text-sm font-medium text-gold-300 hover:bg-noir-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-noir-900"
        @click="navigateToSearch(inputValue)"
      >
        {{ t('search.go') }}
      </button>
    </div>

    <SearchSuggestionsDropdown
      v-if="isDropdownVisible && (hasSuggestions || loading)"
      :suggestions="suggestions"
      :selected-index="selectedIndex"
      :loading="loading"
      :listbox-id="suggestionsListboxId"
      @hover="selectedIndex = $event"
      @select="handleSuggestionSelect($event.title, $event.url)"
    />
  </div>
</template>
